// verificationsClient — AUTHENTICATED staff client for the Phase 4 rental-
// verification REVIEW surface. Uses the shared `api` helper (Bearer token +
// 401 refresh-retry). Distinct from verificationClient.ts (singular), which is
// the PUBLIC token-gated previous-landlord form client.
//
// Wire shapes mirror apps/api/src/routes/rentalVerificationsReview.ts and the
// RentalVerificationRecord in apps/api/src/lib/rentalVerifications.ts.

import { api } from './api';

export type RentalVerificationStatus =
  | 'created'
  | 'sending' // WEL-24 #1: transient — a watcher run has claimed this record for send
  | 'skipped'
  | 'whp-internal-review'
  | 'no-contact'
  | 'sent'
  | 'reminded'
  | 'responded'
  | 'declined'
  | 'denied-tenancy'
  | 'related-shortcut'
  | 'bounced'
  | 'no-response';

export interface VerificationAddress {
  line1: string;
  line2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
}

export interface ResidenceClaim {
  address: VerificationAddress;
  current: boolean;
  residedFrom?: string;
  residedTo?: string;
  monthlyRent?: number;
}

export interface LandlordContact {
  name?: string;
  phone?: string;
  email?: string;
  source: 'provided' | 'pm-override' | 'whp-internal' | 'staff-edited';
  pmDirectoryId?: string;
  overrodeProvidedEmail?: string;
}

export interface LandlordResponse {
  submittedAt: string;
  rentedHere: boolean;
  deniedTenancy?: boolean;
  declined?: boolean;
  declineReason?: string;
  related?: boolean;
  relationshipExplain?: string;
  confirmed?: {
    stillResiding?: boolean;
    moveIn?: string;
    moveOut?: string;
    monthlyRent?: number;
  };
  screening?: {
    othersCount?: number;
    properNotice?: boolean;
    properNoticeTerms?: string;
    eviction?: boolean;
    evictionExplain?: string;
    nsfOrLate?: boolean;
    nsfLateDetail?: string;
    outstandingDebt?: boolean;
    debtDetail?: string;
    leaseViolations?: boolean;
    violationsExplain?: string;
    pets?: boolean;
    petsDetail?: string;
    otherConcerns?: boolean;
    otherConcernsDetail?: string;
    rentAgain?: boolean;
    rentAgainWhyNot?: string;
  };
  respondent?: { name?: string; email?: string; phone?: string };
}

export interface RentalVerificationRecord {
  groupId: string;
  sk: string;
  status: RentalVerificationStatus;
  skipReason?: string;
  signals?: string[];
  applicantIds: string[];
  applicantNames: string[];
  claimed: ResidenceClaim;
  variants?: Array<{ applicantId: string; monthlyRent?: number; residedFrom?: string; residedTo?: string }>;
  landlord: LandlordContact;
  // WEL-24 cross-tenancy landlord dedup + reconcile metadata.
  landlordKey?: string;
  /** Set on a secondary same-landlord record → the primary sk that carries the send. */
  dedupedToSk?: string;
  /** On the primary: other addresses this same landlord is being asked about. */
  alsoVerifyingAddresses?: string[];
  /** Set when staff hand-edited the landlord (sticky vs AppFolio reconcile). */
  staffEditedLandlordAt?: string;
  sentAt?: string;
  response?: LandlordResponse;
  redFlags?: string[];
  verdict?: 'verified' | 'concerns';
  verdictAt?: string;
  verdictBy?: string;
  /** Set when staff cleared the row color flag ("Mark reviewed") on this record. */
  flagClearedAt?: string;
  flagClearedBy?: string;
  updatedAt: string;
}

// WEL-18: AppFolio status mirrored from the API (RentalApplicationStatus). The
// pill labels/tones in VerificationsPage match the Applications tab.
export type AppFolioApplicationStatus =
  | 'new'
  | 'decision_pending'
  | 'in_review'
  | 'in_screening'
  | 'info_needed'
  | 'approved'
  | 'denied'
  | 'canceled';

// WEL-18: slim AppFolio enrichment the API attaches to each review-queue group
// so the Verifications table renders Property / Unit / Applicants / Status like
// the Applications tab. All fields nullable — a group not found in the mirror
// still renders with just its groupId.
export interface VerificationGroupApplicant {
  id: string;
  firstName: string;
  lastName: string;
  isPrimary: boolean;
}

export interface VerificationGroupSummary {
  groupId: string;
  /**
   * WEL-63: is the application/group still ACTIVE in AppFolio's pipeline? Same
   * notion as the Applications page's "active groups" — true ⇒ active; false ⇒
   * "past" (denied / cancelled / converted / aged-out). Drives the Active / Past
   * / All filter on the Verifications page. Optional for backward-compat with an
   * API that predates the field — a missing value is treated as ACTIVE.
   */
  active?: boolean;
  total: number;
  redFlagCount: number;
  concernsCount: number;
  skippedCount: number;
  needsAttention: boolean;
  /**
   * "Color-flag" review-flag clear: true when the group has records AND every
   * record was acknowledged (flagClearedAt). A flagged row is `needsAttention &&
   * !flagCleared` → renders the attention color. Optional for backward-compat
   * with an API that predates the field — a missing value is treated as NOT
   * cleared (so a not-clean group still flags).
   */
  flagCleared?: boolean;
  records: RentalVerificationRecord[];
  /**
   * WEL-23: set ONLY on a record-less active group (total === 0). Splits the two
   * record-less states: 'pending-scan' (the watcher hasn't minted records yet —
   * realtime resweep will, ~1 min) vs 'no-history' (scanned, nothing verifiable).
   * Groups with records leave this undefined.
   */
  pendingState?: 'pending-scan' | 'no-history';
  // WEL-18 enrichment (mirrors RentalVerificationGroupEnrichment on the API).
  appfolioStatus: AppFolioApplicationStatus | null;
  property: { id: string; name: string | null; address: string | null } | null;
  unit: { id: string; name: string | null; address: string | null } | null;
  applicants: VerificationGroupApplicant[];
}

export interface VerificationsListResponse {
  count: number;
  groups: VerificationGroupSummary[];
  records: RentalVerificationRecord[];
}

export async function listVerifications(): Promise<VerificationsListResponse> {
  return api.get<VerificationsListResponse>('/api/rental-verifications');
}

export async function setVerdict(
  groupId: string,
  sk: string,
  verdict: 'verified' | 'concerns',
): Promise<RentalVerificationRecord> {
  const r = await api.post<{ record: RentalVerificationRecord }>(
    `/api/rental-verifications/${encodeURIComponent(groupId)}/${encodeURIComponent(sk)}/verdict`,
    { verdict },
  );
  return r.record;
}

export async function sendSkipped(
  groupId: string,
  sk: string,
): Promise<RentalVerificationRecord> {
  const r = await api.post<{ record: RentalVerificationRecord }>(
    `/api/rental-verifications/${encodeURIComponent(groupId)}/${encodeURIComponent(sk)}/send-skipped`,
  );
  return r.record;
}

// WEL-24: correct a verification's landlord contact (applicant entered the wrong
// info) and, by default, re-send the request to the corrected contact.
export interface EditLandlordBody {
  name?: string;
  /** '' clears the email; omit to leave unchanged. */
  email?: string;
  phone?: string;
  /** Default true on the server — re-send after editing. */
  resend?: boolean;
}

export async function editLandlord(
  groupId: string,
  sk: string,
  body: EditLandlordBody,
): Promise<RentalVerificationRecord> {
  const r = await api.post<{ record: RentalVerificationRecord }>(
    `/api/rental-verifications/${encodeURIComponent(groupId)}/${encodeURIComponent(sk)}/edit-landlord`,
    body,
  );
  return r.record;
}

// WEL-24: re-send the request to the CURRENT landlord (e.g. the landlord declined
// by accident, or asked for the link again).
export async function resendVerification(
  groupId: string,
  sk: string,
): Promise<RentalVerificationRecord> {
  const r = await api.post<{ record: RentalVerificationRecord }>(
    `/api/rental-verifications/${encodeURIComponent(groupId)}/${encodeURIComponent(sk)}/resend`,
  );
  return r.record;
}

// "Mark reviewed" — clear the row color flag for a not-clean group once staff
// have opened it. Stamps flagClearedAt on every record of the group so the
// group reads as fully cleared (flagCleared) on the next refetch.
export async function clearRvFlag(groupId: string): Promise<RentalVerificationRecord[]> {
  const r = await api.post<{ groupId: string; cleared: number; records: RentalVerificationRecord[] }>(
    `/api/rental-verifications/${encodeURIComponent(groupId)}/clear-flag`,
  );
  return r.records;
}
